import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCarePlan } from "../../../services/patient/carePlan";
import { getPatientGeneralDetails } from "../../../services/patient/details";
import { convertUTCToLocal, formatDob, formatMonthYear } from "../../../utils/helper";
import Button from "../../../components/button-v1";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { saveAs } from "file-saver";
import logo from "../../../assets/images/toca_health_pdf_logo.png";

import Spinner from "../../spinner";

import styles from "./style.module.css";
import generateFinalizeCareplanPDF from "./FinalizeCareplanPDF";
import { buildReportFilename, DOCTYPES } from "../../../utils/fileNaming";
import { SYMPTOM_STATES, parseSymptomsString, wasCreatedOnOrAfterSymptomsCutoff } from "../../../utils/symptomTriState";
import { parseSegmentsWithDateTags } from "../../../utils/parseBoldMarkers";
import DeltaDateTag from "../patient-modals/DeltaDateTag";

const FinalizeCareplanPDFView = () => {
  const [params] = useSearchParams();
  const [patient_id, setPatientId] = useState(sessionStorage.getItem("_pt_id"));
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const monthParam = params.get("month");
  const yearParam = params.get("year");

  const parsedMonth = monthParam ? parseInt(monthParam, 10) : null;
  const parsedYear = yearParam ? parseInt(yearParam, 10) : null;

  const [selectedMonth, setSelectedMonth] = useState({
    month:
      parsedMonth && parsedMonth >= 1 && parsedMonth <= 12
        ? parsedMonth
        : currentMonth,
    year: parsedYear && parsedYear > 0 ? parsedYear : currentYear,
  });

  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const [downloading, setDownloading] = useState(false);

  const { data: patient, isLoading: patientLoading } = useQuery(
    ["patient-general-details", patient_id],
    () => getPatientGeneralDetails(patient_id),
    { enabled: !!patient_id }
  );

  const { data: carePlanData, isFetching: carePlan_loading } = useQuery({
    queryKey: ["carePlan", patient_id],
    queryFn: () => getCarePlan(selectedMonth.month, selectedMonth.year),
    enabled: !!patient_id,
  });

  useEffect(() => {
    const patientId = params.get("patientId");
    if (patientId) {
      setPatientId(patientId);
    }
  }, [params]);
 

  const patientName = `${patient?.user?.first_name || ""} ${patient?.user?.last_name || ""}`;

  // Backend stamps assigned_at when the care plan is completed, and keeps it
  // once the plan is finalized.
  const completedAt = carePlanData?.data?.assigned_at;

  const nurseName = (patient?.nurse?.first_name && patient?.nurse?.last_name)
    ? `${patient.nurse.first_name} ${patient.nurse.last_name}`
    : (patient?.nurse?.first_name || patient?.nurse?.last_name || "");

  const reportDateDisplay = formatMonthYear(new Date(selectedMonth.year, selectedMonth.month - 1));

const handleDownloadPDF = async () => {
  setDownloading(true);
  const toastId = toast.loading("Generating PDF...");

  try {
    // Generate the PDF
    const pdfDoc = await generateFinalizeCareplanPDF({
      patient,
      carePlanData: carePlanData?.data,
      conditions: billedConditions,
      medications: carePlanData?.data?.medications,
      reportDate: reportDateDisplay, // numeric month for display
      logo,
    });

    // Convert PDF to blob
    const blob = pdfDoc.output("blob");

    const fileName = buildReportFilename({
      year: selectedMonth.year,
      month: selectedMonth.month,
      patientFirstName: patient?.user?.first_name,
      patientLastName: patient?.user?.last_name,
      doctype: DOCTYPES.CAREPLAN,
      program: "CCM",
      ext: "pdf",
    });

    // Save the PDF
    saveAs(blob, fileName);

    toast.success("PDF Downloaded!", { id: toastId });
  } catch (error) {
    console.error("PDF generation failed:", error);
    toast.error("Failed to generate PDF", { id: toastId });
  } finally {
    setDownloading(false);
  }
};


  const isLoading =
    patientLoading ||
    carePlan_loading;

  if (isLoading) {
    return <div className={styles.loadingContainer}><Spinner /></div>;
  }

  // Mirrors careplan-view-pdf/index.jsx's renderBoldText - AI-generated
  // Careplan Notes/Recommendations mark emphasis with literal
  // "**double asterisks**", rendered bold (black, per direct request) here
  // too, plus the "(added/updated MM/DD/YYYY)" date-tag chip (SCRUM-788)
  // split out of the bold text by parseSegmentsWithDateTags.
  const renderBoldText = (text) =>
    parseSegmentsWithDateTags(text).map((segment, i) =>
      segment.bold ? (
        <strong key={i} style={{ fontWeight: 700, color: "#000000" }}>
          {segment.text}
          <DeltaDateTag dateTag={segment.dateTag} />
        </strong>
      ) : (
        <React.Fragment key={i}>{segment.text}</React.Fragment>
      )
    );

  // Mirrors careplan-view-pdf/index.jsx's renderSymptoms - same two-state
  // glyph/color (SCRUM-755), for this PDF preview's own markup.
  const SYMPTOM_GLYPH = {
    [SYMPTOM_STATES.YES]: { glyph: "✓", color: "#10b981" },
    [SYMPTOM_STATES.NO]: { glyph: "X", color: "#ef4444" },
  };

  // Figma label/value field: small grey label over the navy value.
  const renderInfoField = (label, value) => (
    <div className={styles.infoField}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={styles.infoValue}>{value}</span>
    </div>
  );

  // Figma bordered list box: blue title row, then numbered items laid out
  // left-to-right across `columns` columns.
  const renderListBox = (title, items, columns = 2, emptyContent = null) => (
    <div className={styles.listBox}>
      <div className={styles.listBoxTitle}>{title}</div>
      {items.length > 0 ? (
        <div
          className={`${styles.listBoxGrid} ${columns === 2 ? styles.listBoxGridTwo : ""}`}
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {items.map((item, i) => (
            <div key={i} className={styles.listBoxItem}>
              <span className={styles.listBoxIndex}>{i + 1}.</span>
              <span>{item}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.listBoxItem}>{emptyContent}</div>
      )}
    </div>
  );

  // Figma unboxed numbered list under a subsection heading: items flow
  // left-to-right across 3 columns with grey dividers between columns.
  const renderNumberedGrid = (items) =>
    items.length > 0 ? (
      <div className={styles.numberedGrid}>
        {items.map((item, i) => (
          <div key={i} className={styles.numberedGridItem}>
            <span className={styles.listBoxIndex}>{i + 1}.</span>
            <span>{item}</span>
          </div>
        ))}
      </div>
    ) : null;

  // Free-text fields (Prognosis, Treatment Goals, ...) split into their
  // non-blank lines so each line becomes one numbered item.
  const textLines = (text) =>
    (text || "").split("\n").map((line) => line.trim()).filter(Boolean);

  // Single-column numbered list under a subsection heading (Figma: Prognosis).
  const renderNumberedLines = (items) =>
    items.length > 0 ? (
      <div className={styles.numberedLines}>
        {items.map((item, i) => (
          <div key={i} className={styles.numberedGridItem}>
            <span className={styles.listBoxIndex}>{i + 1}.</span>
            <span>{item}</span>
          </div>
        ))}
      </div>
    ) : null;

  // Symptoms for the list box: same check/X glyph and qualifier rules as
  // renderSymptoms; reported (YES) symptoms are bold per the Figma design.
  const renderSymptomItems = (text) =>
    parseSymptomsString(text || "")
      .filter((symptom) => symptom.name.trim())
      .map((symptom, i) => {
        const { glyph, color } = SYMPTOM_GLYPH[symptom.state];
        const display = symptom.qualifier ? `${symptom.name} — ${symptom.qualifier}` : symptom.name;
        return (
          <span key={i} className={symptom.state === SYMPTOM_STATES.YES ? styles.symptomReported : undefined}>
            {symptom.qualifier ? null : <span style={{ color, fontWeight: 700, marginRight: "5px" }}>{glyph}</span>}
            {renderBoldText(display)}
          </span>
        );
      });

  const renderCheckboxLabel = (checked, label) => (
    <span className={styles.checkboxLabel}>
      <span className={`${styles.checkboxBox} ${checked ? styles.checkboxBoxChecked : ""}`} />
      <span>{label}</span>
    </span>
  );

  const getBehavioralVal = (key1, key2) => {
  const value =
    carePlanData?.data?.behavioral_data?.[key1] ||
    carePlanData?.data?.behavioral_data?.[key2] ||
    "";
  return ["--", "None", "N/A"].includes(value) ? "" : value;
};

  const formatStatus = (status) => {
  if (["--", "None", "N/A"].includes(status)) {
    return "";
  }
  const statusMap = {
    finalized: "Finalized",
    in_progress: "In Progress",
    completed: "Completed"
  };

  return statusMap[status] || status || "";
};

const convertDate = (date) => {
  const value = convertUTCToLocal(date);
  return ["--", "None", "N/A"].includes(value) ? "" : value;
};

const formatDateOfBirth = (date) => {
  const value = formatDob(date);
  return ["--", "None", "N/A"].includes(value) ? "" : value;
};

  // Filter chronic conditions to show only those marked for billing
  const billedConditions = (carePlanData?.data?.chronic_conditions || []).filter((cond) => cond?.for_billing_icd === true);
  return (
    <>
      <section
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "50rem",
          padding: "20px 0 20px 0",
        }}
      >
        <Button
          loading={downloading}
          disabled={downloading}
          id="download-btn"
          onClick={handleDownloadPDF}
        >
          Download PDF
        </Button>
      </section>

      {/* Screen Preview */}
      <div className={styles.container} id="care-plan-preview">
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTopRow}>
            <div className={styles.logoContainer}>
              <img src={logo} alt="TOCA Health" className={styles.logo} />
              <div className={styles.headerSeparator}></div>
              <span className={styles.locationName}>{patient?.location?.name || ""}</span>
            </div>
            <div className={styles.headerMeta}>
              <span className={styles.reportDate}>{reportDateDisplay}</span>
              <span className={styles.headerMetaDate}>
                Completed date: {convertDate(completedAt)}
              </span>
            </div>
          </div>
          <div className={styles.headerTitleRow}>
            <h1 className={styles.reportTitle}>CCM Careplan</h1>
            <span className={styles.headerMetaDate}>
              Finalized date: {convertDate(carePlanData?.data?.finalized_at)}
            </span>
          </div>
        </div>

        {/* Patient Information */}
        <div className={styles.infoSection}>
          <div className={styles.infoSectionHeader}>Patient Information</div>
          <div className={styles.infoGrid}>
            <div className={styles.infoColumn}>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>Patient Name :</span>
                <span className={styles.infoValue}>{patientName}</span>
              </div>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>Case Manager :</span>
                <span className={styles.infoValue}>
                  {patient?.case_manager?.first_name || patient?.case_manager?.last_name
                    ? `${patient.case_manager.first_name || ""} ${patient.case_manager.last_name || ""}`.trim()
                    : ""}
                </span>
              </div>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>Referring Physician :</span>
                <span className={styles.infoValue}>
                  {patient?.provider
                    ? `${patient?.provider?.first_name || ""} ${patient?.provider?.last_name || ""}`.trim() || ""
                    : ""}
                </span>
              </div>
            </div>
            <div className={styles.infoColumn}>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>DOB :</span>
                <span className={styles.infoValue}>{formatDateOfBirth(patient?.user?.dob)}</span>
              </div>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>Status :</span>
                <span className={styles.infoValue}>{formatStatus(carePlanData?.data?.status)}</span>
              </div>
            </div>
            <div className={styles.infoColumn}>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>Phone :</span>
                <span className={styles.infoValue}>{patient?.user?.primary_phone || ""}</span>
              </div>
              <div className={styles.infoField}>
                <span className={styles.infoLabel}>Nurse :</span>
                <span className={styles.infoValue}>{nurseName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Careplan Details */}
        <div className={styles.infoSection}>
          <div className={styles.infoSectionHeader}>Careplan Details</div>
          <div className={styles.infoGrid}>
            <div className={styles.infoColumn}>
              {renderInfoField("Created At :", convertDate(carePlanData?.data?.created_at))}
              {renderInfoField("Finalized By :", carePlanData?.data?.finalized_by_name || carePlanData?.data?.finalized_by || "")}
            </div>
            <div className={styles.infoColumn}>
              {renderInfoField("Created By :", carePlanData?.data?.created_by_name || carePlanData?.data?.created_by || "")}
              {renderInfoField("Last Updated At :", convertDate(carePlanData?.data?.updated_at))}
            </div>
            <div className={styles.infoColumn}>
              {renderInfoField("Finalized At :", convertDate(carePlanData?.data?.finalized_at))}
              {renderInfoField("Complex :", carePlanData?.data?.complex ? "Yes" : "No")}
            </div>
          </div>
          <div className={styles.listBoxGroup}>
            {renderListBox(
              "Chronic Condition",
              billedConditions.map((cond) => `${cond?.name || "Unknown"}-(${cond?.icd_code || "N/A"})`)
            )}
          </div>
        </div>

        {/* 3- Checkbox */}
        {/* <table className={styles.sectionTable}>
          <thead>
            <tr>
              <th colSpan="4" className={styles.sectionTableHeader}>3- Checks</th>
            </tr>
          </thead>
          <tbody className={styles.sectionTableBody}>
            <tr className={styles.sectionTableRow}>
              <td className={`${styles.sectionTableCell} ${styles.sectionTableValueFull}`} colSpan="4">
                <div className={styles.checkboxRow}>
                  {renderCheckboxLabel(!!carePlanData?.data?.dob_confirmed, "DOB Confirmed")}
                  {renderCheckboxLabel(!!carePlanData?.data?.has_chronic_conditions, "Has at least 2 Chronic Conditions")}
                  {renderCheckboxLabel(!!carePlanData?.data?.consent_for_calls, "Consent for monthly calls")}
                </div>
              </td>
            </tr>
          </tbody>
        </table> */}


        {/* Medication */}
        <div className={styles.infoSection}>
          <div className={styles.infoSectionHeader}>Medication</div>
          <div className={styles.listBoxGroup}>
            {(() => {
              const medicationItems = Array.isArray(carePlanData?.data?.medications)
                ? carePlanData?.data?.medications
                : (carePlanData?.data?.medications ? [carePlanData?.data?.medications] : []);

              const lines = medicationItems
                .flatMap((m) => (m?.name || "").split(/\r?\n/))
                .map((l) => (l || "").replace(/\s*=\s*undefined\s*$/i, "").trim())
                .filter((l) => l && !["--", "None", "N/A"].includes(l));

              return renderListBox(
                "Medicines",
                lines.map((line) => renderBoldText(line)),
                2,
                <span className={styles.emptyText}>No medications recorded.</span>
              );
            })()}
            <div className={styles.listBoxRow}>
              {renderListBox(
                "Allergies",
                (carePlanData?.data?.allergies?.length > 0 ? carePlanData.data.allergies : [])
                  .map((item) => (item || "").toString().replace(/\s*=\s*undefined\s*$/i, "").trim())
                  .filter((item) => item)
                  .map((item) => renderBoldText(item)),
                1
              )}
              {renderListBox(
                "Side Effects",
                (carePlanData?.data?.reported_side_effect?.length > 0 ? carePlanData.data.reported_side_effect : [])
                  .map((item) => (item || "").toString().replace(/\s*=\s*undefined\s*$/i, "").trim())
                  .filter((item) => item)
                  .map((item) => renderBoldText(item)),
                1
              )}
            </div>
          </div>
        </div>

        {/* Health Summary */}
        <div className={styles.infoSection}>
          <div className={styles.infoSectionHeader}>Health Summary</div>

          <div className={styles.subSectionHeading}>Physiological Averages</div>
          <div className={styles.infoGrid}>
            <div className={styles.infoColumn}>
              {renderInfoField("Blood Pressure :", carePlanData?.data?.physiological_data?.blood_pressure || "")}
              {renderInfoField("Blood Glucose :", carePlanData?.data?.physiological_data?.blood_glucose || "")}
            </div>
            <div className={styles.infoColumn}>
              {renderInfoField("Height :", carePlanData?.data?.physiological_data?.height || "")}
              {renderInfoField("BMI :", carePlanData?.data?.physiological_data?.bmi || "")}
            </div>
            <div className={styles.infoColumn}>
              {renderInfoField("Weight :", carePlanData?.data?.physiological_data?.weight || "")}
            </div>
          </div>

          {/* Bug fix: these 3 values rendered the raw string directly (no
              renderBoldText), so any "**bold**(added/updated MM/DD/YYYY)"
              AI markers in Sleep/Exercise/Diet-Water showed up as literal
              asterisks instead of bold + the date-tag chip. */}
          <div className={styles.subSectionHeading}>Behavioral Averages</div>
          <div className={styles.infoGrid}>
            <div className={styles.infoColumn}>
              {renderInfoField("Sleep :", renderBoldText(getBehavioralVal('sleep_hours', 'sleep')))}
            </div>
            <div className={styles.infoColumn}>
              {renderInfoField("Exercise :", renderBoldText(getBehavioralVal('exercise_hours_per_week', 'exercise')))}
            </div>
            <div className={styles.infoColumn}>
              {renderInfoField("Diet / Water :", renderBoldText(getBehavioralVal('water_intake', 'water_diet')))}
            </div>
          </div>
        </div>

        {/* Problem List & Education */}
        <div className={styles.infoSection}>
          <div className={styles.infoSectionHeader}>Problem List &amp; Education</div>

          <div className={styles.subSectionHeading}>PMHx</div>
          {renderNumberedGrid((carePlanData?.data?.pmhx || []).map((item) => renderBoldText(item)))}

          <div className={styles.subSectionHeading}>Potential Barriers</div>
          {renderNumberedGrid((carePlanData?.data?.potential_barriers || []).map((item) => renderBoldText(item)))}

          <div className={styles.subSectionHeading}>Recent Hospitalization</div>
          {renderNumberedGrid((carePlanData?.data?.recent_hospitalization || []).map((item) => renderBoldText(item)))}

          <div className={styles.listBoxGroup}>
            {renderListBox(
              "Recommendations",
              (carePlanData?.data?.recommendations || []).map((item) => renderBoldText(item))
            )}
          </div>
        </div>

        {/* Functional Assessment */}
        <div className={styles.infoSection}>
          <div className={styles.infoSectionHeader}>Functional Assessment</div>
          <div className={styles.listBoxGroup}>
            {renderListBox(
              "ADLS",
              (carePlanData?.data?.activities_of_daily_living || []).map((item) => renderBoldText(item))
            )}
            {renderListBox(
              "Caregiver Assessment",
              (carePlanData?.data?.caregiver_assessment || []).map((item) => renderBoldText(item))
            )}
          </div>
        </div>

        {/* Chronic Conditions - one section per billed condition */}
        {billedConditions?.length > 0 ? (
          billedConditions.map((cond, index) => (
            <div key={index} className={styles.infoSection}>
              <div className={styles.infoSectionHeader}>
                <span>
                  Chronic Conditions #{index + 1} -{" "}
                  <strong className={styles.infoSectionHeaderStrong}>
                    {`${cond?.name || ""}${cond?.icd_code ? ` - (${cond.icd_code})` : ""}`}
                  </strong>
                </span>
              </div>

              <div className={styles.subSectionHeading}>Prognosis</div>
              {renderNumberedLines(textLines(cond.prognosis).map((line) => renderBoldText(line)))}

              <div className={styles.listBoxGroup}>
                {renderListBox(
                  "Symptoms",
                  wasCreatedOnOrAfterSymptomsCutoff(carePlanData?.data?.created_at)
                    ? renderSymptomItems(cond.symptoms)
                    : textLines(cond.symptoms).map((line) => renderBoldText(line))
                )}
                {renderListBox(
                  "Treatment Goals",
                  textLines(cond.treatment_goals).map((line) => renderBoldText(line))
                )}
                {renderListBox(
                  "Planned Interventions",
                  textLines(cond.planned_interventions).map((line) => {
                    const parts = line.split(":::");
                    return renderBoldText(parts.length > 1 ? `${parts[0]}: ${parts[1]}` : line);
                  })
                )}
              </div>
            </div>
          ))
        ) : (
          <div className={styles.infoSection}>
            <div className={styles.infoSectionHeader}>Chronic Conditions</div>
            <div className={styles.notesText}>
              <span className={styles.emptyText}>No chronic conditions recorded.</span>
            </div>
          </div>
        )}

        {/* Careplan Notes */}
        <div className={styles.infoSection}>
          <div className={styles.infoSectionHeader}>Careplan Notes</div>
          <div className={`${styles.notesText} ${styles.preWrap}`}>
            {carePlanData?.data?.note ? renderBoldText(carePlanData.data.note) : "No notes available."}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.pageFooter}>
          <div className={styles.pageFooterColumn}>
            <span>
              Nurse <strong>{nurseName}</strong>
              {"  |  "}Reviewed by <strong>{carePlanData?.data?.finalized_by_name || carePlanData?.data?.finalized_by || ""}</strong>
              {"  |  "}Finalised <strong>{convertDate(carePlanData?.data?.finalized_at)}</strong>
            </span>
            <span>
              {patientName}{"  |  "}{patient?.location?.name || ""}{"  |  "}CCM Careplan
            </span>
          </div>
          <div className={`${styles.pageFooterColumn} ${styles.pageFooterRight}`}>
            <span>
              Downloaded at <strong>{convertUTCToLocal(new Date().toISOString(), navigator.language, true)}</strong>
            </span>
            <span>1 of 1</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default FinalizeCareplanPDFView;